import type { AppConfig } from '../config';
import type { NewCallLogEntry } from '../calllog/calllog';
import type { CallStatus, Direction, RegistrationState, TransportState } from '../state/store';
import type { Handler, SessionLike, UAFactory, UALike } from './types';

export interface PhoneEvents {
  registration: { state: RegistrationState; reason?: 'auth' | 'other'; cause?: string };
  transport: { state: TransportState };
  incoming: { from: string; displayName?: string };
  callState: { state: CallStatus | 'ended'; direction: Direction; cause?: string };
  callEnded: NewCallLogEntry;
  remoteStream: { stream: MediaStream };
}

export interface Phone {
  connect(c: { extension: string; password: string }): void;
  disconnect(): void;
  call(target: string): void;
  answer(): void;
  reject(): void;
  hangup(): void;
  setMuted(muted: boolean): void;
  setHeld(held: boolean): void;
  sendDtmf(tone: string): void;
  hasCall(): boolean;
  on<K extends keyof PhoneEvents>(event: K, handler: (payload: PhoneEvents[K]) => void): () => void;
}

interface ActiveCall {
  session: SessionLike;
  direction: Direction;
  number: string;
  displayName?: string;
  startedAt: number;
  answeredAt?: number;
  answering?: boolean;
  rejectedLocally: boolean;
}

const AUTH_CAUSES = new Set(['Authentication Error', 'Rejected']);
// Nach Verbindungsfehlern registriert JsSIP beim Reconnect selbst neu.
const NO_RETRY_CAUSES = new Set([...AUTH_CAUSES, 'Connection Error']);
const RETRY_MIN_MS = 2_000;
const RETRY_MAX_MS = 60_000;
const MEDIA = { audio: true, video: false } as const;
const ICE_MAX_WAIT_MS = 4_000;

export function createPhone(deps: { config: AppConfig; createUA: UAFactory; now?: () => number }): Phone {
  const now = deps.now ?? Date.now;
  const listeners = new Map<keyof PhoneEvents, Set<(p: unknown) => void>>();
  let ua: UALike | null = null;
  let current: ActiveCall | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryDelay = RETRY_MIN_MS;
  const cancelRetry = () => {
    if (retryTimer) clearTimeout(retryTimer);
    retryTimer = null;
  };

  const emit = <K extends keyof PhoneEvents>(event: K, payload: PhoneEvents[K]) => {
    for (const l of listeners.get(event) ?? []) l(payload);
  };
  const iceServers: RTCIceServer[] = deps.config.stunUrls.map((urls) => ({ urls }));
  if (deps.config.turn) iceServers.push({ ...deps.config.turn });
  const mediaOptions = () => ({
    mediaConstraints: MEDIA,
    pcConfig: { iceServers },
  });

  const watchPeerConnection = (pc: RTCPeerConnection | null | undefined) => {
    if (!pc) return;
    pc.addEventListener('track', (ev: Event) => {
      const { streams, track } = ev as RTCTrackEvent;
      // Ohne a=msid liefert der Browser keine streams – dann selbst einen bauen.
      const stream = streams?.[0] ?? (track && typeof MediaStream !== 'undefined' ? new MediaStream([track]) : undefined);
      if (stream) emit('remoteStream', { stream });
    });
  };

  const finish = (call: ActiveCall, kind: 'ended' | 'failed', cause?: string) => {
    let result: NewCallLogEntry['result'];
    if (kind === 'ended') result = 'answered';
    else if (call.direction === 'out') result = 'failed';
    else if (call.rejectedLocally) result = 'rejected';
    else result = 'missed';
    const durationSec = call.answeredAt ? Math.round((now() - call.answeredAt) / 1000) : 0;
    const entry: NewCallLogEntry = {
      direction: call.direction,
      number: call.number,
      startedAt: call.startedAt,
      durationSec,
      result,
    };
    if (call.displayName) entry.displayName = call.displayName;
    if (current === call) current = null;
    emit(
      'callState',
      cause ? { state: 'ended', direction: call.direction, cause } : { state: 'ended', direction: call.direction },
    );
    emit('callEnded', entry);
  };

  const attach = (call: ActiveCall) => {
    const s = call.session;
    s.on('peerconnection', (e: { peerconnection: RTCPeerConnection }) => watchPeerConnection(e.peerconnection));
    // JsSIP schickt INVITE bzw. 200 OK erst, wenn die ICE-Suche komplett fertig ist. Mit TURN über
    // mehrere Netzwerkschnittstellen (z. B. LAN + VPN) dauert das in Chrome teils minutenlang.
    // Daher abschicken, sobald ein Relay-Kandidat da ist, spätestens nach ICE_MAX_WAIT_MS.
    let iceRound: (() => void) | null = null;
    let iceTimer: ReturnType<typeof setTimeout> | null = null;
    const clearIceTimer = () => {
      if (iceTimer) clearTimeout(iceTimer);
      iceTimer = null;
    };
    s.on('icecandidate', (e: { candidate: { candidate: string }; ready: () => void }) => {
      if (e.ready !== iceRound) {
        clearIceTimer();
        iceRound = e.ready;
        iceTimer = setTimeout(e.ready, ICE_MAX_WAIT_MS);
      }
      if (/\styp relay(\s|$)/.test(e.candidate.candidate)) {
        clearIceTimer();
        e.ready();
      }
    });
    s.on('ended', clearIceTimer);
    s.on('failed', clearIceTimer);
    s.on('accepted', () => {
      if (call.answeredAt) return;
      call.answeredAt = now();
      emit('callState', { state: 'established', direction: call.direction });
    });
    s.on('hold', () => emit('callState', { state: 'held', direction: call.direction }));
    s.on('unhold', () => emit('callState', { state: 'established', direction: call.direction }));
    s.on('ended', (e: { cause?: string }) => finish(call, 'ended', e?.cause));
    s.on('failed', (e: { cause?: string }) => finish(call, 'failed', e?.cause));
  };

  const onNewSession = (e: { originator: string; session: SessionLike }) => {
    if (e.originator !== 'remote') return; // ausgehende Sessions verwaltet call()
    const from = e.session.remote_identity.uri.user;
    const displayName = e.session.remote_identity.display_name || undefined;
    if (current) {
      e.session.terminate({ status_code: 486, reason_phrase: 'Busy Here' });
      const entry: NewCallLogEntry = { direction: 'in', number: from, startedAt: now(), durationSec: 0, result: 'missed' };
      if (displayName) entry.displayName = displayName;
      emit('callEnded', entry);
      return;
    }
    const call: ActiveCall = { session: e.session, direction: 'in', number: from, startedAt: now(), rejectedLocally: false };
    if (displayName) call.displayName = displayName;
    current = call;
    attach(call);
    emit('incoming', displayName ? { from, displayName } : { from });
    emit('callState', { state: 'ringing-in', direction: 'in' });
  };

  return {
    connect({ extension, password }) {
      cancelRetry();
      retryDelay = RETRY_MIN_MS;
      const previous = ua;
      ua = null;
      previous?.stop();
      const thisUa = deps.createUA({
        wssUrl: deps.config.wssUrl,
        uri: `sip:${extension}@${deps.config.sipDomain}`,
        authorizationUser: extension,
        password,
      });
      ua = thisUa;
      // JsSIP feuert nach stop() noch verspätet (z. B. 'disconnected' nach dem un-REGISTER);
      // Events einer nicht mehr aktuellen UA werden verworfen.
      const on = (event: string, handler: Handler) =>
        thisUa.on(event, (...args: unknown[]) => {
          if (ua === thisUa) handler(...args);
        });
      on('connected', () => emit('transport', { state: 'connected' }));
      on('disconnected', () => emit('transport', { state: 'disconnected' }));
      on('registered', () => {
        cancelRetry();
        retryDelay = RETRY_MIN_MS;
        emit('registration', { state: 'registered' });
      });
      on('unregistered', () => emit('registration', { state: 'unregistered' }));
      on('registrationFailed', (e: { cause?: string }) => {
        const cause = e?.cause ?? 'unknown';
        emit('registration', { state: 'failed', reason: AUTH_CAUSES.has(cause) ? 'auth' : 'other', cause });
        if (NO_RETRY_CAUSES.has(cause) || ua !== thisUa) return;
        cancelRetry();
        retryTimer = setTimeout(() => {
          retryTimer = null;
          if (ua === thisUa) thisUa.register();
        }, retryDelay);
        retryDelay = Math.min(retryDelay * 2, RETRY_MAX_MS);
      });
      on('newRTCSession', onNewSession);
      emit('registration', { state: 'registering' });
      thisUa.start();
    },
    disconnect() {
      cancelRetry();
      current?.session.terminate();
      current = null;
      const previous = ua;
      ua = null;
      previous?.stop();
    },
    call(target) {
      if (!ua) throw new Error('Nicht verbunden');
      if (current) return;
      const session = ua.call(`sip:${target}@${deps.config.sipDomain}`, mediaOptions());
      const call: ActiveCall = { session, direction: 'out', number: target, startedAt: now(), rejectedLocally: false };
      current = call;
      attach(call);
      watchPeerConnection(session.connection);
      emit('callState', { state: 'calling', direction: 'out' });
    },
    answer() {
      // JsSIP wirft beim zweiten answer() (z. B. Doppelklick während der Mikrofon-Abfrage).
      if (current?.direction !== 'in' || current.answeredAt || current.answering) return;
      current.answering = true;
      current.session.answer(mediaOptions());
    },
    reject() {
      if (current?.direction === 'in' && !current.answeredAt) {
        current.rejectedLocally = true;
        current.session.terminate({ status_code: 603, reason_phrase: 'Decline' });
      }
    },
    hangup() {
      current?.session.terminate();
    },
    setMuted(muted) {
      if (!current) return;
      if (muted) current.session.mute({ audio: true });
      else current.session.unmute({ audio: true });
    },
    setHeld(held) {
      if (!current?.answeredAt) return;
      if (held) current.session.hold();
      else current.session.unhold();
    },
    sendDtmf(tone) {
      if (current?.answeredAt) current.session.sendDTMF(tone, { transportType: 'RFC2833' });
    },
    hasCall: () => current !== null,
    on(event, handler) {
      const set = listeners.get(event) ?? new Set();
      set.add(handler as (p: unknown) => void);
      listeners.set(event, set);
      return () => set.delete(handler as (p: unknown) => void);
    },
  };
}
